import { useCallback, useEffect, useRef, useState } from 'react'

import { type Enum } from '@webapp/infrastructure/types/Enum'
import { runConversationTurn } from '../../../business-logic/use-cases/RunConversationTurnUseCase'
import { type ConversationTelemetryEvent } from '../../../value-objects/ConversationTelemetryEvent'
import {
  createConversationTurnItem,
  type ConversationTurnItem,
  type ConversationTurnItemConstructor,
} from '../../../value-objects/ConversationTurnItem'
import { ConversationItemKinds } from '../../../value-objects/enums'

export const ConversationStreamStatuses = {
  IDLE: 'idle',
  STREAMING: 'streaming',
  DONE: 'done',
  ERROR: 'error',
} as const

export type ConversationStreamStatusEnum = Enum<
  typeof ConversationStreamStatuses
>

export interface UseConversationStreamResult {
  items: ConversationTurnItem[]
  status: ConversationStreamStatusEnum
  telemetry: ConversationTelemetryEvent[]
  sendMessage: (message: string) => void
}

export const useConversationStream = (
  initialQuery: string
): UseConversationStreamResult => {
  const [items, setItems] = useState<ConversationTurnItem[]>([])
  const [status, setStatus] = useState<ConversationStreamStatusEnum>(
    ConversationStreamStatuses.IDLE
  )
  const [telemetry, setTelemetry] = useState<ConversationTelemetryEvent[]>([])

  const nextIdRef = useRef(0)
  const disconnectRef = useRef<Nullable<() => void>>(null)
  const conversationIdRef = useRef<Nullable<string>>(null)
  const isStreamingRef = useRef(false)

  const appendItem = useCallback(
    (constructor: ConversationTurnItemConstructor) => {
      setItems((prev) => [
        ...prev,
        createConversationTurnItem(constructor, nextIdRef.current++),
      ])
    },
    []
  )

  const upsertItem = useCallback(
    (constructor: ConversationTurnItemConstructor) => {
      if (constructor.kind !== ConversationItemKinds.ENVELOPE) {
        appendItem(constructor)
        return
      }
      setItems((prev) => {
        const index = prev.findIndex(
          (item) =>
            item.kind === ConversationItemKinds.ENVELOPE &&
            item.envelopeId === constructor.envelopeId
        )
        if (index === -1) {
          return [
            ...prev,
            createConversationTurnItem(constructor, nextIdRef.current++),
          ]
        }
        const next = prev.slice()
        next[index] = createConversationTurnItem(constructor, prev[index].id)
        return next
      })
    },
    [appendItem]
  )

  const markEnvelopesAnswered = useCallback(() => {
    setItems((prev) =>
      prev.map((item) =>
        item.kind === ConversationItemKinds.ENVELOPE && !item.isAnswered
          ? { ...item, isAnswered: true }
          : item
      )
    )
  }, [])

  const openTurn = useCallback(
    (message: string) => {
      setStatus(ConversationStreamStatuses.STREAMING)
      isStreamingRef.current = true
      disconnectRef.current?.()
      disconnectRef.current = runConversationTurn(
        { message, conversationId: conversationIdRef.current },
        {
          onItem: upsertItem,
          onSessionStart: (conversationId) => {
            conversationIdRef.current = conversationId
          },
          onTelemetry: (event) => setTelemetry((prev) => [...prev, event]),
          onDone: () => {
            isStreamingRef.current = false
            setStatus((prev) =>
              prev === ConversationStreamStatuses.ERROR
                ? prev
                : ConversationStreamStatuses.DONE
            )
          },
          onError: (error) => {
            isStreamingRef.current = false
            appendItem({
              kind: ConversationItemKinds.ERROR,
              message: error.message ?? error.code,
            })
            setStatus(ConversationStreamStatuses.ERROR)
          },
        }
      )
    },
    [appendItem, upsertItem]
  )

  const sendMessage = useCallback(
    (rawMessage: string) => {
      if (isStreamingRef.current) return

      const message = rawMessage.trim()
      if (message === '') return

      markEnvelopesAnswered()
      appendItem({
        kind: ConversationItemKinds.MESSAGE,
        sender: 'user',
        content: message,
      })
      openTurn(message)
    },
    [appendItem, markEnvelopesAnswered, openTurn]
  )

  useEffect(() => {
    openTurn(initialQuery)
    return () => disconnectRef.current?.()
  }, [initialQuery, openTurn])

  return { items, status, telemetry, sendMessage }
}
